import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Collapse, Modal, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import Spinner from 'react-spinkit';
import { branch, compose, lifecycle, mapProps, renderComponent, withHandlers, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators } from 'redux';
import { exportCpdActivityMap, getCpdActivityTitle, getCpdActivityTransform, invitationStatus, mapCpdPoint, routes, tabKeys, userRoles } from '../../../../../constants';
import cpdPointStatus from '../../../../../constants/cpd-point-status';
import { defaultQueryTeamMemberConfig, fullName, isExternalUser, notCanceled } from '../../../../../services';
import { businessActions, businessSelector, inactiveUserCpdActivitySelector, isFetchingInactiveCpdActivitiesSelector, queryTeamMembersSelector } from '../../../../../state/modules/business';
import { cpdPointActions } from '../../../../../state/modules/cpd-points';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, pluralise, writeDataToCsvStream } from '../../../../../utils';
import { ConfirmationModal, ControlGroup, ControlOption, Icon, SelectFormField, TypeaheadField, UserAvatar } from '../../../../components';
import Loading from '../../../loading';
import { ResendInvitation, TeamInvitations } from '../components';

const columns = fp.flow(
  fp.keys,
  fp.map((key) => [key, getCpdActivityTitle(key)(exportCpdActivityMap)]),
  fp.fromPairs
)(exportCpdActivityMap);

const mapInactiveUserCpdActivity = (cpdCategorys, publishers) =>
  fp.flow(
    fp.filter((cpdPoint) => cpdPoint.status !== cpdPointStatus.draft),
    fp.map((cpdPoint) =>
      fp.flow(
        fp.keys,
        fp.map((key) => getCpdActivityTransform(key)(exportCpdActivityMap)(mapCpdPoint(cpdCategorys, publishers)(cpdPoint)))
      )(exportCpdActivityMap)
    ));

const userStatusProps = (status) => ({
  key: status.value,
  value: status.value,
  label: <span>{status.value}</span>,
  checked: status.checked
})

const InvitationOptions = ({
  invitationId,
  confirmRemoveInvitation,
  toggleRemoveInvitationModal,
  toggleResendInvitationModal,
  removingInvitation,
  resendingInvitation,
  resendingInvitationEmail,
  email,
  name,
  firstName,
  lastName
}) => (
    [
      <Col key={0} xs={12} sm={3}>
        <p>
          <ResendInvitation
            show={resendingInvitation === invitationId}
            closeModal={toggleResendInvitationModal}
            invitationId={resendingInvitation}
            user={{ email, firstName, lastName }}
          />
          <Button bsStyle='plain' className='full align-right'
            onClick={() => toggleResendInvitationModal(invitationId, email)}>
            Resend invitation
          </Button>
        </p>
      </Col>,
      <Col key={1} xs={12} sm={3}>
        <p>
          <ConfirmationModal
            show={removingInvitation === invitationId}
            actions={[{
              onConfirmAction: () => confirmRemoveInvitation(invitationId),
              actionLabel: 'Yes, remove invitation',
            }]}
            cancel={toggleRemoveInvitationModal}
            headingText={
              <span>
                You're removing invitation
              </span>
            }
            message={
              <div>
                <p>Are you sure you want to remove the invitation to <b>{email}</b>.</p>
                <p>The user can no longer accept the invitation after you remove it.</p>
              </div>
            }
            cancelLabel={'Cancel'} />
          <Button
            bsStyle='plain'
            className='full align-right warning'
            onClick={() => toggleRemoveInvitationModal(invitationId)}>
            Remove invitation
          </Button>
        </p>
      </Col>
    ]
  );

const InactiveUserOptions = ({
  id,
  exportInactiveUserCSV
}) => (
    <Col key={0} xs={12} smOffset={3} sm={3}>
      <p>
        <Button bsStyle='plain' className='full align-right'
          onClick={() => exportInactiveUserCSV(id)}>
          Export CPD activity history
        </Button>
      </p>
    </Col>
  )

const ConfirmationAddAdminModalMessage = ({ name }) => (
  <p>
    By granting <b>{name}</b> account administrator access they will be able to edit payment information,
    invite and delete team members.
  </p>
);

const ConfirmationRemoveAdminModalMessage = ({ name }) => (
  <p>
    By removing <b>{name}</b> account administrator access they will no longer be able to edit payment information,
    invite and delete team members
  </p>
);

const ConfirmationAddManagerModalMessage = ({ name }) => (
  <p>
    By granting <b>{name}</b> training plan manager access they will be excluded from training plans
  </p>
);

const ConfirmationRemoveManagerModalMessage = ({ name }) => (
  <p>
    By removing <b>{name}</b> training plan manager access they will be included in training plans
  </p>
);

const ConfirmationRemoveModalMessage = ({ name }) => (
  [
    <p key={0}>
      Are you sure you want to remove <b>{name}</b> from your business account?
    </p>,
    <p key={1}>
      Once you remove <b>{name}</b>, the number of user licenses under your business will automatically update and you will no longer be charged for their account.
    </p>
  ]
);

const memberRoleActions = {
  setAdmin: 'setAdmin',
  unsetAdmin: 'unsetAdmin',
  setManager: 'setManager',
  unsetManager: 'unsetManager'
}

const BusinessAdminOptions = ({
  role,
  userId,
  name,
  removingManagerUser,
  addingManagerUser,
  dismissToggleManagerModal,
  toggleAddManagerModal,
  toggleRemoveManagerModal,
  confirmRemoveBusinessTrainingPlanManager,
  confirmAddBusinessTrainingPlanManager
}) => {
  return <Col xs={12} sm={3}>
    {
      fp.includes(userRoles.BusinessAdmin)(role) ?
        fp.includes(userRoles.Manager)(role) ?
          <form className='memberRoleActions'>
            <ConfirmationModal
              show={removingManagerUser === userId}
              actions={[{
                onConfirmAction: () => confirmRemoveBusinessTrainingPlanManager(userId),
                actionLabel: 'Remove training plan manager',
              }]}
              cancel={dismissToggleManagerModal}
              message={<ConfirmationRemoveManagerModalMessage {...{ name }} />}
              headingText={<span>Remove training plan manager</span>}
              cancelLabel={'Cancel'} />
            <SelectFormField
              emptyOption={false}
              value={memberRoleActions.setManager}
              onChange={(e) => {
                const action = e.target.value
                if (action === memberRoleActions.unsetManager) {
                  toggleRemoveManagerModal(userId)
                }
              }}>
              <option value={memberRoleActions.unsetManager}>Business Admin</option>
              <option value={memberRoleActions.setManager}>Training plan manager</option>
            </SelectFormField>
          </form> :
          <form className='memberRoleActions'>
            <ConfirmationModal
              show={addingManagerUser === userId}
              actions={[{
                onConfirmAction: () => confirmAddBusinessTrainingPlanManager(userId),
                actionLabel: 'Make training plan manager',
              }]}
              cancel={dismissToggleManagerModal}
              message={<ConfirmationAddManagerModalMessage {...{ name }} />}
              headingText={<span>Make team member training plan manager</span>}
              cancelLabel={'Cancel'} />
            <SelectFormField
              emptyOption={false}
              value={memberRoleActions.unsetManager}
              onChange={(e) => {
                const action = e.target.value
                if (action === memberRoleActions.setManager) {
                  toggleAddManagerModal(userId)
                }
              }}>
              <option value={memberRoleActions.unsetManager}>Business Admin</option>
              <option value={memberRoleActions.setManager}>Training plan manager</option>
            </SelectFormField>
          </form> :
        null
    }
  </Col>
}

const UserOptions = compose(
  branch(
    ({ status }) => status === 'Pending',
    renderComponent(InvitationOptions)
  ),
  branch(
    ({ status }) => status === 'Inactive',
    renderComponent(InactiveUserOptions)
  ),
  branch(
    ({ userId, currentUser, role }) => userId === currentUser.id && fp.includes(userRoles.BusinessAdmin)(role),
    renderComponent(BusinessAdminOptions)
  )
)(
  ({
    currentUser,
    role,
    userId,
    name,
    toggleRemoveUserModal,
    toggleAddAdminUserModal,
    toggleRemoveAdminUserModal,
    confirmRemoveUser,
    confirmAddBusinessAdmin,
    confirmRemoveBusinessAdmin,
    dismissRemoveUserModal,
    dismissToggleAdminModal,
    removingUser,
    addingAdminUser,
    removingAdminUser,
    isExternalUser,
    addingManagerUser,
    dismissToggleManagerModal,
    toggleAddManagerModal,
    confirmAddBusinessTrainingPlanManager
  }) => {
    const isAdmin = fp.includes(userRoles.BusinessAdmin)(role)

    const isManager = fp.includes(userRoles.Manager)(role)

    const activeRole = () => {
      if (isManager) {
        return memberRoleActions.setManager;
      }

      if (isAdmin) {
        return memberRoleActions.setAdmin;
      }

      return memberRoleActions.unsetAdmin;
    }

    return [
      <Col key={0} xs={12} sm={3}>
        <form className='memberRoleActions'>
          <ConfirmationModal
            show={addingManagerUser === userId}
            actions={[{
              onConfirmAction: () => confirmAddBusinessTrainingPlanManager(userId),
              actionLabel: 'Make training plan manager',
            }]}
            cancel={dismissToggleManagerModal}
            message={<ConfirmationAddManagerModalMessage {...{ name }} />}
            headingText={<span>Make team member training plan manager</span>}
            cancelLabel={'Cancel'} />
          <ConfirmationModal
            show={removingAdminUser === userId}
            actions={[{
              onConfirmAction: () => confirmRemoveBusinessAdmin(userId),
              actionLabel: 'Remove administrator',
            }]}
            cancel={dismissToggleAdminModal}
            message={<ConfirmationRemoveAdminModalMessage {...{ name }} />}
            headingText={<span>Revoke administrator privledges</span>}
            cancelLabel={'Cancel'} />
          <ConfirmationModal
            show={addingAdminUser === userId}
            actions={[{
              onConfirmAction: () => confirmAddBusinessAdmin(userId),
              actionLabel: 'Make administrator',
            }]}
            cancel={dismissToggleAdminModal}
            message={<ConfirmationAddAdminModalMessage {...{ name }} />}
            headingText={<span>Make team member account administrator</span>}
            cancelLabel={'Cancel'} />
          <SelectFormField
            emptyOption={false}
            value={activeRole()}
            onChange={(e) => {
              const action = e.target.value
              if (action === memberRoleActions.setAdmin) {
                toggleAddAdminUserModal(userId)
              }
              if (action === memberRoleActions.unsetAdmin) {
                toggleRemoveAdminUserModal(userId)
              }
              if (action === memberRoleActions.setManager) {
                toggleAddManagerModal(userId)
              }
            }}>
            <option value={memberRoleActions.unsetAdmin}>Business User</option>
            <option value={memberRoleActions.setAdmin}>Administrator</option>
            <option value={memberRoleActions.setManager}>Training plan manager</option>
          </SelectFormField>
        </form>
      </Col>,
      isExternalUser ? null :
        < Col key={1} xs={12} sm={3} >
          <p>
            <ConfirmationModal
              show={removingUser === userId}
              actions={[{
                onConfirmAction: () => confirmRemoveUser(userId),
                actionLabel: 'Yes, remove user from business',
              }]}
              cancel={dismissRemoveUserModal}
              message={<ConfirmationRemoveModalMessage {...{ name }} />}
              headingText={<span>You're removing {name} from your business.</span>}
              cancelLabel={'Cancel'} />
            <Button
              bsStyle='plain'
              className='full align-right warning'
              onClick={() => toggleRemoveUserModal(userId)}>
              Remove from plan
            </Button>
          </p>
        </Col >
    ]
  }
);

const UserListLoader = () => (
  <Row className='no-gutter text-center'>
    <Col xs={12}>
      <Loading />
    </Col>
  </Row>
);

const UserList = branch(
  ({ loading }) => loading,
  renderComponent(UserListLoader)
)(({
  confirmRemoveInvitation,
  confirmRemoveUser,
  confirmAddBusinessAdmin,
  confirmRemoveBusinessAdmin,
  currentUser,
  dismissRemoveUserModal,
  dismissToggleAdminModal,
  removingInvitation,
  resendingInvitation,
  removingUser,
  addingAdminUser,
  removingAdminUser,
  toggleButtons,
  toggleRemoveUserModal,
  toggleAddAdminUserModal,
  toggleRemoveAdminUserModal,
  toggleRemoveInvitationModal,
  toggleResendInvitationModal,
  users,
  exportInactiveUserCSV,
  isExternalUser,
  toggleAddManagerModal,
  toggleRemoveManagerModal,
  dismissToggleManagerModal,
  confirmAddBusinessTrainingPlanManager,
  confirmRemoveBusinessTrainingPlanManager,
  addingManagerUser,
  removingManagerUser
}) => (
    fp.isEmpty(users) ?
      <Row className={classNames('no-gutter striped')}>
        <Col xs={12}>
          <p className='text-center'>
            No users found
          </p>
        </Col>
      </Row> :
      fp.flow(
        fp.map(({
          id,
          name,
          userId,
          email,
          firstName,
          lastName,
          role,
          status,
          showButtonsOn,
          invitationId,
          avatar
        }) => (
            <Row
              key={id}
              className={classNames('no-gutter striped', { showButtonsOn })}>
              <Col xs={12} sm={6}>
                <div className='left'>
                  <UserAvatar userName={name} avatar={avatar} />
                </div>
                <p>
                  <Link to={routes.app.teams.userProfiles(userId)}>{name}</Link>
                  <br />
                  <small>
                    {email}
                  </small>
                </p>
                {
                  userId === currentUser.id ?
                    null :
                    <Icon
                      icon='option'
                      className='showButtons'
                      onClick={() => toggleButtons(id)} />
                }
              </Col>
              <UserOptions {...{
                id,
                role,
                status,
                userId,
                name,
                email,
                firstName,
                lastName,
                currentUser,
                toggleRemoveUserModal,
                toggleAddAdminUserModal,
                toggleRemoveAdminUserModal,
                confirmRemoveUser,
                confirmAddBusinessAdmin,
                confirmRemoveBusinessAdmin,
                dismissRemoveUserModal,
                dismissToggleAdminModal,
                removingUser,
                addingAdminUser,
                removingAdminUser,
                invitationId,
                confirmRemoveInvitation,
                toggleRemoveInvitationModal,
                toggleResendInvitationModal,
                removingInvitation,
                resendingInvitation,
                exportInactiveUserCSV,
                isExternalUser,
                toggleAddManagerModal,
                toggleRemoveManagerModal,
                dismissToggleManagerModal,
                confirmAddBusinessTrainingPlanManager,
                confirmRemoveBusinessTrainingPlanManager,
                addingManagerUser,
                removingManagerUser
              }} />
            </Row>
          ))
      )(users)
  )
);

const UserManagement = ({
  confirmRemoveUser,
  confirmAddBusinessAdmin,
  confirmRemoveBusinessAdmin,
  dismissRemoveUserModal,
  dismissToggleAdminModal,
  displayInviteModal,
  confirmRemoveInvitation,
  currentUser,
  goTo,
  currentSubscription: {
    id: hasCurrentSubscription,
    quantity
  },
  loading,
  removingInvitation,
  resendingInvitation,
  removingUser,
  addingAdminUser,
  removingAdminUser,
  searchMembers,
  teamSize,
  toggleRemoveInvitationModal,
  toggleResendInvitationModal,
  toggleInviteUserModal,
  toggleRemoveUserModal,
  toggleAddAdminUserModal,
  toggleRemoveAdminUserModal,
  toggleButtons,
  users,
  inactiveUsers,
  exportInactiveUserCSV,
  setStatusFilter,
  statusFilter,
  updateStatus,
  userStatusFilter,
  teamMembers,
  totalUserCount,
  invitations,
  hasMoreToLoad,
  loadMore,
  isExternalUser,
  toggleAddManagerModal,
  toggleRemoveManagerModal,
  dismissToggleManagerModal,
  confirmAddBusinessTrainingPlanManager,
  confirmRemoveBusinessTrainingPlanManager,
  addingManagerUser,
  removingManagerUser
}) => (
    <Row className='no-gutter'>
      {
        isExternalUser ?
          <Col xs={12} sm={7}></Col> :
          <Col xs={12} sm={7}>
            <h2>
              You've got {quantity} {pluralise('Caddie user license', quantity)} for your business, including {totalUserCount} active {pluralise('user', totalUserCount)} & {invitations.length} pending {pluralise('invitation', invitations.length)}.
            </h2>
            <p>
              You can add and remove users below and your payment details will be updated accordingly.
            </p>
          </Col>
      }
      <Col xs={12} sm={4} smOffset={1}>
        <p>
          {
            hasCurrentSubscription ?
              <Modal show={displayInviteModal} onHide={toggleInviteUserModal} dialogClassName='invite-team-member-modal'>
                <Modal.Body>
                  <Button onClick={toggleInviteUserModal} className='pull-right full-width' bsStyle='plain'>
                    <Icon icon='close' />
                  </Button>
                  <TeamInvitations
                    displayExistingInvitations={false}
                    confirmText={'Confirm invite'}
                    postConfirmAction={toggleInviteUserModal}
                    cancelText={'Cancel'}
                    cancelAction={toggleInviteUserModal} />
                </Modal.Body>
              </Modal> :
              <ConfirmationModal
                show={displayInviteModal}
                actions={[{
                  onConfirmAction: () => goTo(routes.app.accountSettings(tabKeys.accountSettings.paymentDetail)),
                  actionLabel: 'Open payment options',
                }]}
                cancel={toggleInviteUserModal}
                message={<p>You can re-enable your subscription on the Payment Details page.</p>}
                headingText={<span>You cannot invite users as your subscription is inactive</span>}
                cancelLabel={`Not now, I'll do this later`} />
          }
          {
            isExternalUser ? null :
              <Button className='inline pull-right sm-full' onClick={toggleInviteUserModal}>
                <Icon icon='add-large' />
                &nbsp;
                Invite users to plan
              </Button>
          }
        </p>
        <form>
          <TypeaheadField {...searchMembers} />
        </form>
      </Col>
      <div className="accordion--filter">
        <Row id="status-filter-title" className='button-gutter'>
          <Col xs={12}>
            <h2 type='button' onClick={setStatusFilter} aria-expanded={statusFilter} aria-controls='status-filter'>Filter by status <Icon icon='minus-large'></Icon></h2>
          </Col>
        </Row>
        <Collapse id="status-filter" in={statusFilter}>
          <form noValidate>
            <fieldset>
              <ControlGroup
                id='content-status'
                control='checkbox'
                type='styled'
                label={null}
                onChange={(e) => updateStatus(e)}
                colProps={{ xs: 12, sm: 3 }}>
                {
                  fp.map(status =>
                    <ControlOption {...userStatusProps(status)} />
                  )(userStatusFilter)
                }
              </ControlGroup>
            </fieldset>
          </form>
        </Collapse>
      </div>
      <Col xs={12}>
        <Row
          className={classNames('no-gutter userlist-header')}>
          <Col xs={12} sm={6}>
            <span>User Name</span>
          </Col>
          <Col xs={12} sm={3}>
            <span>Role</span>
          </Col>
        </Row>
      </Col>
      <div className='clearfix divider hide-sm' />
      <Col xs={12}>
        <UserList {...{
          confirmRemoveInvitation,
          confirmRemoveUser,
          confirmAddBusinessAdmin,
          confirmRemoveBusinessAdmin,
          currentUser,
          dismissRemoveUserModal,
          dismissToggleAdminModal,
          loading,
          removingInvitation,
          resendingInvitation,
          removingUser,
          addingAdminUser,
          removingAdminUser,
          toggleButtons,
          toggleRemoveInvitationModal,
          toggleResendInvitationModal,
          toggleRemoveUserModal,
          toggleAddAdminUserModal,
          toggleRemoveAdminUserModal,
          users,
          exportInactiveUserCSV,
          isExternalUser,
          toggleAddManagerModal,
          toggleRemoveManagerModal,
          dismissToggleManagerModal,
          confirmAddBusinessTrainingPlanManager,
          confirmRemoveBusinessTrainingPlanManager,
          addingManagerUser,
          removingManagerUser
        }} />
      </Col>
      {
        hasMoreToLoad ?
          <Col xs={12} className='flex-center'>
            {
              loading ?
                <Button><Spinner name='circle' className='spinner' fadeIn='none' /></Button> :
                <Button onClick={loadMore}>Load More</Button>
            }
          </Col> : null
      }
    </Row>
  );

const mapStateToProps = (state) => ({
  invitations: fp.filter(({ status }) => fp.eq(status)(invitationStatus.Sent))(state.business.invitations),
  loadingInvitations: state.business.loadingInvitations,
  loadingUsers: state.business.isFetchingTeamMembers,
  inactiveUsers: state.business.inactiveUsers,
  currentUser: state.user.currentUser,
  business: businessSelector(state),
  currentSubscription: notCanceled(state.business.subscription),
  exportActivity: inactiveUserCpdActivitySelector(state),
  isFetchingInactiveCpdActivities: isFetchingInactiveCpdActivitiesSelector(state),
  teamMembersResult: queryTeamMembersSelector(state),
  teamMembers: fp.flow(
    fp.get('teamMembers'),
    fp.map(({ user, roleAssignments }) => ({
      ...user,
      role: fp.map(fp.get('role'))(roleAssignments)
    }))
  )(queryTeamMembersSelector(state))
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  removeMember: bindActionCreators(businessActions.business.removeMember, dispatch),
  addBusinessAdmin: bindActionCreators(businessActions.business.addBusinessAdmin, dispatch),
  getTeamMembers: bindActionCreators(businessActions.business.queryTeamMembers, dispatch),
  loadMoreTeamMembers: bindActionCreators(businessActions.business.loadMoreTeamMembers, dispatch),
  removeBusinessAdmin: bindActionCreators(businessActions.business.removeBusinessAdmin, dispatch),
  deleteInvitation: bindActionCreators(businessActions.business.deleteInvitation, dispatch),
  getInactiveUsers: bindActionCreators(businessActions.business.getInactiveUsers, dispatch),
  fetchInactiveCpdActivities: bindActionCreators(businessActions.business.getInactiveUserCpdActivity, dispatch),
  getExportCpdPoints: bindActionCreators(cpdPointActions.cpdPoints.getExport, dispatch),
  addBusinessManager: bindActionCreators(businessActions.business.addBusinessManager, dispatch),
  removeBusinessManager: bindActionCreators(businessActions.business.removeBusinessManager, dispatch)
});

const userItem = ({ name, email }) => `${name} - (${email})`;

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        getInactiveUsers,
        business: {
          id
        },
        getTeamMembers
      } = this.props;
      getInactiveUsers(id)
      getTeamMembers(id, {
        take: defaultQueryTeamMemberConfig.take,
        managers: true
      });
    }
  }),
  withProps(({
    invitations,
    loadingInvitations,
    loadingUsers,
    teamMembersResult,
    currentUser
  }) => ({
    isExternalUser: isExternalUser(currentUser),
    totalUserCount: fp.get('total')(teamMembersResult),
    loading: loadingInvitations || loadingUsers,
    minLength: 300
  })),
  withStateHandlers({
    displayInviteModal: false,
    removingInvitation: null,
    resendingInvitation: null,
    removingUser: null,
    addingAdminUser: null,
    removingAdminUser: null,
    addingManagerUser: null,
    removingManagerUser: null,
    searchText: '',
    showButtons: [],
    fetchCpdActivitiesUserID: undefined,
    statusFilter: false,
    userStatusFilter: {
      Active: { value: 'Active', checked: true },
      Pending: { value: 'Pending', checked: true },
      Inactive: { value: 'Inactive', checked: false }
    }
  }, {
      dismissRemoveUserModal: () => () => ({
        removingUser: null
      }),
      dismissToggleAdminModal: () => () => ({
        addingAdminUser: null,
        removingAdminUser: null
      }),
      dismissToggleManagerModal: () => () => ({
        addingManagerUser: null,
        removingManagerUser: null
      }),
      setSearchText: () => (searchText) => ({
        searchText
      }),
      toggleButtons: ({ showButtons }) => (id) => ({
        showButtons: fp.includes(id)(showButtons) ?
          fp.remove(fp.eq(id))(showButtons) :
          fp.concat(id)(showButtons)
      }),
      toggleInviteUserModal: ({ displayInviteModal }) => () => ({
        displayInviteModal: !displayInviteModal
      }),
      toggleRemoveInvitationModal: () => (invitationId) => ({
        removingInvitation: invitationId
      }),
      toggleResendInvitationModal: () => (invitationId, email) => ({
        resendingInvitation: invitationId
      }),
      toggleRemoveUserModal: () => (userId) => ({
        removingUser: userId
      }),
      toggleAddAdminUserModal: () => (userId) => ({
        addingAdminUser: userId
      }),
      toggleRemoveAdminUserModal: () => (userId) => ({
        removingAdminUser: userId
      }),
      toggleAddManagerModal: () => (userId) => ({
        addingManagerUser: userId
      }),
      toggleRemoveManagerModal: () => (userId) => ({
        removingManagerUser: userId
      }),
      setStatusFilter: (prevState) => () => ({
        statusFilter: !prevState.statusFilter
      }),
      updateStatus: (prevState) => (e) => ({
        userStatusFilter: {
          ...prevState.userStatusFilter,
          [e.target.value]: { value: e.target.value, checked: e.target.checked }
        }
      })
    }
  ),
  withProps(({
    invitations,
    showButtons,
    teamMembers,
    searchText
  }) => ({
    userList: fp.flow(
      fp.spread(fp.concat),
      fp.map(({ id, role, ...user }) => ({
        ...user,
        id: `${role ? 'user' : 'invitation'}-${id}`,
        userId: role ? id : null,
        invitationId: role ? null : id,
        name: `${fullName(user)}${role ? '' : ' (Pending invite)'}`,
        status: role ? 'Active' : 'Pending',
        role
      })),
      fp.map(({ id, ...user }) => ({
        ...user,
        id,
        showButtonsOn: fp.includes(id)(showButtons)
      }))
    )([teamMembers, invitations])
  })),
  withHandlers({
    onChange: ({
      setSearchText
    }) => ([user]) => {
      if (user) {
        const searchText = userItem(user);
        setSearchText(searchText);
      }
    },
    onInputChange: ({
      setSearchText
    }) => setSearchText,
    confirmRemoveUser: ({
      dismissRemoveUserModal,
      removeMember,
      getInactiveUsers,
      business
    }) => (userId) => {
      removeMember(business.id, { userId });
      dismissRemoveUserModal();
    },
    confirmAddBusinessAdmin: ({
      dismissToggleAdminModal,
      addBusinessAdmin,
      business
    }) => (userId) => {
      addBusinessAdmin(business.id, { userId });
      dismissToggleAdminModal();
    },
    confirmRemoveBusinessAdmin: ({
      dismissToggleAdminModal,
      removeBusinessAdmin,
      business
    }) => (userId) => {
      removeBusinessAdmin(business.id, { userId });
      dismissToggleAdminModal();
    },
    confirmAddBusinessTrainingPlanManager: ({
      dismissToggleManagerModal,
      addBusinessManager,
      business
    }) => (userId) => {
      addBusinessManager(business.id, { userId });
      dismissToggleManagerModal();
    },
    confirmRemoveBusinessTrainingPlanManager: ({
      dismissToggleManagerModal,
      removeBusinessManager,
      business
    }) => (userId) => {
      removeBusinessManager(business.id, { userId });
      dismissToggleManagerModal();
    },
    confirmRemoveInvitation: ({
      business,
      deleteInvitation,
      toggleRemoveInvitationModal
    }) => (invitationId) => {
      deleteInvitation(business.id, invitationId);
      toggleRemoveInvitationModal();
    },
    exportInactiveUserCSV: ({ business, fetchInactiveCpdActivities }) => (teamMemberId) => {
      fetchInactiveCpdActivities(business.id, teamMemberId)
    }
  }),
  withProps(({
    searchText,
    userList,
    inactiveUsers,
    showButtons
  }) => ({
    users: userList,
    inactiveUsers: inactiveUsers.map(({
      id,
      email,
      firstName,
      lastName
    }) => ({
      id,
      email,
      name: `${firstName} ${lastName} (Inactive)`,
      status: 'Inactive',
      showButtonsOn: fp.includes(id)(showButtons)
    }))
  })),
  withProps(
    ({ searchText, teamMembersResult, userStatusFilter, teamMembers }) => {
      const active = userStatusFilter.Active.checked
      const pending = userStatusFilter.Pending.checked
      const inactive = userStatusFilter.Inactive.checked
      return ({
        userFilter: {
          tags: searchText,
          take: defaultQueryTeamMemberConfig.take,
          active,
          pending,
          inactive,
          managers: true
        },
        hasMoreToLoad: teamMembers.length < fp.get('hits')(teamMembersResult)
      })
    }
  ),
  withHandlers({
    loadMore: ({ loadMoreTeamMembers, business, userFilter, teamMembers }) => () => {
      loadMoreTeamMembers(business.id, {
        ...userFilter,
        skip: teamMembers.length
      })
    }
  }),
  withStateHandlers(
    {
      searchDebounce: undefined,
      frontEndFilter: ''
    },
    {
      updateSearchDebounce: () => (searchDebounce) => ({
        searchDebounce
      }),
      applyFilter: (_, { searchText }) => () => ({
        frontEndFilter: searchText
      })
    }
  ),
  lifecycle({
    componentWillReceiveProps({
      isFetchingInactiveCpdActivities,
      publishers,
      exportActivity,
      cpdCategorys,
      searchText,
      userFilter,
      business,
      getTeamMembers,
      loadingUsers,
      applyFilter,
      frontEndFilter
    }) {
      if (this.props.isFetchingInactiveCpdActivities && !isFetchingInactiveCpdActivities &&
        exportActivity
      ) {
        downloadBlobStreamFile(writeDataToCsvStream(getCsvStream({
          header: true,
          columns
        }), mapInactiveUserCpdActivity(cpdCategorys, publishers)(fp.uniqBy('userCpdPointId')(exportActivity.userCpdPoints))).pipe(getBlobStream()), 'cpd-activity.csv');
      }
      if (!fp.equals(this.props.userFilter)(userFilter)) {
        const {
          updateSearchDebounce,
          searchDebounce
        } = this.props
        const debounced = fp.debounce(500)(() => {
          getTeamMembers(business.id, userFilter);
        });
        updateSearchDebounce(debounced)
        searchDebounce && searchDebounce.cancel();
        debounced();
      }
      if (loadingUsers && !this.props.loadingUsers) {
        applyFilter()
      }
    }
  }),
  withProps(
    ({
      userList,
      inactiveUsers,
      userStatusFilter,
      frontEndFilter
    }) => ({
      statusFilteredUsers: fp.flow(
        fp.filter(
          user => {
            return fp.get('checked')(fp.get(user.status)(userStatusFilter))
          }
        ),
        fp.sortBy([
          ({ status }) => status === 'Active' ? 0 : status === 'Pending' ? 1 : 2,
          'name',
          'email'
        ]),
        fp.filter(
          ({ name }) => fp.contains(fp.toLower(frontEndFilter))(fp.toLower(name))
        )
      )(fp.concat(userList)(inactiveUsers))
    })
  ),
  mapProps(({
    loading,
    minLength,
    onChange,
    onInputChange,
    searchText,
    frontEndFilter,
    statusFilteredUsers,
    userList,
    users,
    ...props
  }) => ({
    ...props,
    loading,
    searchMembers: {
      label: 'Search members',
      icon: 'search-large',
      emptyLabel: loading ?
        'Searching...' :
        'No matches',
      isLoading: loading,
      labelKey: userItem,
      minLength,
      options: statusFilteredUsers,
      onChange,
      onInputChange,
      onSearch: fp.identity,
      renderMenuItemChildren: userItem,
      searchText,
    },
    teamSize: userList.length,
    users: fp.filter(
      fp.flow(
        userItem,
        fp.toLower,
        fp.overSome([
          () => fp.get('length')(frontEndFilter) < minLength,
          fp.includes(fp.toLower(frontEndFilter))
        ])
      )
    )(statusFilteredUsers)
  }))
)(UserManagement);



// WEBPACK FOOTER //
// ./src/containers/pages/app/account-settings/partials/user-management.js